import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../../../contexts/PusherContext';
import { useAgent } from '../../../contexts/AgentContext';
import api from '../../../api';
import {
  AssistantTodoItem,
  GetAllAssistantTodosResponse,
  SubThreadCreatedEvent,
  SubThreadUpdateEvent,
  AgentStreamEvents,
} from '@dreamai/types';
import { CentralTodoList } from '../sidebar/CentralTodoList';
import { useStatusPillHidden } from '../../../contexts/StatusPillContext';
import { ReactComponent as TodoSpinnerIcon } from '../../../icons/todo-spinner.svg';
import { ReactComponent as HourglassIcon } from '../../../icons/hourglass.svg';
import { ReactComponent as PillCaretDownIcon } from '../../../icons/pill-caret-down.svg';
import './agent-status-pill.scss';

interface AgentStatusPillProps {
  threadId: string;
}

const AgentStatusPill: React.FC<AgentStatusPillProps> = ({ threadId }) => {
  const hidden = useStatusPillHidden();
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const { agents } = useAgent();
  const [todos, setTodos] = useState<AssistantTodoItem[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);

  const primaryAgent = useMemo(() => {
    return (
      agents.find((a) => (a.agent_type as string) === 'Personal Assistant') ||
      agents[0]
    );
  }, [agents]);

  const agentNickname =
    primaryAgent?.nickname || primaryAgent?.name || 'Assistant';

  // Fetch todos on mount
  useEffect(() => {
    const fetchTodos = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetAllAssistantTodosResponse>(
          `/todos?parentThreadId=${threadId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setTodos(res.data.todos || []);
      } catch (err) {
        console.error('[AgentStatusPill] Error fetching todos:', err);
      }
    };
    fetchTodos();
  }, [threadId, getAccessTokenSilently]);

  // Listen for Pusher events
  useEffect(() => {
    if (!pusherChannel) return;

    const handleCreated = (data: SubThreadCreatedEvent) => {
      if (!data.subThread || data.parentThreadId !== threadId) return;
      const newTodo: AssistantTodoItem = {
        id: data.subThread.id,
        description: data.subThread.description,
        status: data.subThread.status as any,
        summary: data.subThread.summary,
        createdAt: data.subThread.createdAt,
        updatedAt: data.subThread.updatedAt,
        parentThreadId: data.parentThreadId,
        parentThreadDescription: data.parentThreadDescription || 'Thread',
        parentThreadIsMainThread: data.parentThreadIsMainThread,
        parentThreadIsInternalMonologue: data.parentThreadIsInternalMonologue,
        latestArtifact: (data.subThread as AssistantTodoItem).latestArtifact,
      };
      setTodos((prev) => {
        if (prev.some((t) => t.id === newTodo.id)) return prev;
        return [newTodo, ...prev];
      });
    };

    const handleUpdated = (data: SubThreadUpdateEvent) => {
      if (!data.subThread || data.parentThreadId !== threadId) return;
      setTodos((prev) => {
        const idx = prev.findIndex((t) => t.id === data.subThread.id);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          ...data.subThread,
        } as AssistantTodoItem;
        return updated;
      });
    };

    pusherChannel.bind(AgentStreamEvents.SUB_THREAD_CREATED, handleCreated);
    pusherChannel.bind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdated);
    return () => {
      pusherChannel.unbind(AgentStreamEvents.SUB_THREAD_CREATED, handleCreated);
      pusherChannel.unbind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdated);
    };
  }, [pusherChannel, threadId]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (pillRef.current && !pillRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Find top status todo: processing first, then waiting_on_third_party
  const topTodo = useMemo(() => {
    const processing = todos.find((t) => t.status === 'processing');
    if (processing) return processing;
    const waiting = todos.find((t) => t.status === 'waiting_on_third_party');
    if (waiting) return waiting;
    return null;
  }, [todos]);

  const activeCount = useMemo(
    () =>
      todos.filter(
        (t) => t.status !== 'complete' && t.status !== 'waiting_on_user',
      ).length,
    [todos],
  );

  const toggleDropdown = useCallback(() => {
    setDropdownOpen((prev) => !prev);
  }, []);

  // Determine pill content
  let icon: React.ReactNode = null;
  let label = "I'm not working on anything";

  if (topTodo) {
    const latestItem = topTodo.summary || topTodo.description;

    if (topTodo.status === 'processing') {
      icon = <TodoSpinnerIcon className='status-icon spinner' />;
      label = latestItem;
    } else if (topTodo.status === 'waiting_on_third_party') {
      icon = <HourglassIcon className='status-icon hourglass' />;
      label = latestItem;
    }
  }

  if (hidden) return null;

  return (
    <div className='agent-status-pill-wrapper' ref={pillRef}>
      <div className='agent-status-pill' onClick={toggleDropdown}>
        {icon && <span className='pill-icon'>{icon}</span>}
        <span className='pill-label'>{label}</span>
        {activeCount > 1 && <span className='pill-count'>{activeCount}</span>}
      </div>
      <button
        className={`pill-caret${dropdownOpen ? ' open' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleDropdown();
        }}
      >
        <PillCaretDownIcon />
      </button>
      {dropdownOpen && (
        <div className='agent-status-dropdown'>
          <CentralTodoList
            variant='other'
            threadId={threadId}
            agentName={agentNickname}
          />
        </div>
      )}
    </div>
  );
};

export default AgentStatusPill;
